import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { Button, Box, Paper, SxProps } from '@mui/material';
import { PoiBulkUploadActionTypes, useBulkUploadPoiActions } from '../actions';
import { useAtomValue } from 'jotai';
import * as analysisState from '@/components/Analysis/state';
import { useEffect, useMemo, useRef, useState } from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { AllPointOfInterestTemplate } from '@/types';
import { SUPPORT_ERROR_MESSAGE } from '@/constants';
import {
  BulkUploadPoisInput,
  Exact,
  ValidateBulkPoiUploadQuery,
  useValidateBulkPoiUploadLazyQuery,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { LinkTypography } from './styles';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { CsvValidationException, CsvValidationIssue } from '@/components/Analysis/Idms/types';
import { QueryResult } from '@apollo/client';
import { GraphQLErrorExtensions } from 'graphql';
import { XlsxTemplateProcessor } from '@/utils/xlsx';
import { fileToBase64 } from '@/utils/fileProcessing';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks';
import { enumToString } from '@/utils/string';
const style: SxProps = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  height: '60%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};
type PoiTemplateViewerProps = {
  allPointOfInterestTemplates: AllPointOfInterestTemplate;
  selectedTemplate: AllPointOfInterestTemplate[0];
  setSelectedTemplate: (template: AllPointOfInterestTemplate[0]) => void;
};
const PoiTemplateViewer = ({
  allPointOfInterestTemplates,
  selectedTemplate,
  setSelectedTemplate,
}: PoiTemplateViewerProps) => {
  return (
    <Box sx={{ display: 'flex', columnGap: '1%', my: 3 }}>
      {allPointOfInterestTemplates.map((template) => (
        <Button
          onClick={() => setSelectedTemplate(template)}
          key={template.id}
          variant={selectedTemplate?.id === template?.id ? 'contained' : 'text'}
        >
          {template.name}
        </Button>
      ))}
    </Box>
  );
};

export const PoiTemplateSelectorModal = () => {
  const { state, dispatch } = useBulkUploadPoiActions();
  const fileRef = useRef<HTMLInputElement>(null);
  const [validate, { loading }] = useValidateBulkPoiUploadLazyQuery({});
  const { showSnackBar } = useSnackBarMessage({});
  const structureId = useGetStructureId();

  const allPointOfInterestTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);
  const filteredTemplates = useMemo(
    () => allPointOfInterestTemplates.filter((item) => item.isCsvUploadable),
    [allPointOfInterestTemplates]
  );
  const [selectedTemplate, setSelectedTemplate] = useState(filteredTemplates[0]);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId() ?? '';
  const unitSystem = useAtomValue(analysisState.unitSystem);

  // Ensure that the selected template is the first template in the filtered templates
  useEffect(() => {
    setSelectedTemplate(filteredTemplates[0]);
  }, [filteredTemplates]);

  const isOpen = state.modal === 'upload';

  const handleClose = () => {
    dispatch({ type: PoiBulkUploadActionTypes.CLOSE_ALL_MODALS });
  };

  const onClick = () => {
    fileRef.current?.click();
  };

  const handleResponseErrors = (
    response: QueryResult<
      ValidateBulkPoiUploadQuery,
      Exact<{
        input: BulkUploadPoisInput;
      }>
    >
  ) => {
    const error = response?.error;

    const extensions = error?.graphQLErrors?.[0]?.extensions as GraphQLErrorExtensions;
    if (!(extensions && typeof extensions === 'object' && 'code' in extensions)) {
      // Unhandled error occurred
      // This wont be a validation error, so we show a generic error message
      showSnackBar(error?.message || SUPPORT_ERROR_MESSAGE, 'error');
      return;
    }

    // Typecheck the type of the error, this should never happen
    // but it's good to have a fallback
    if (typeof extensions?.type !== 'string') {
      showSnackBar(error?.message || SUPPORT_ERROR_MESSAGE, 'error');
      return;
    }

    // In case the validation errors does not fall into any of these known categories,
    // resort to showing the error message from the server or a generic error message
    if (!['header', 'filetype', 'validation'].includes(extensions.type)) {
      showSnackBar(error?.message || SUPPORT_ERROR_MESSAGE, 'error');
      return;
    }

    const csvError: CsvValidationException = {
      name: 'CsvValidationException',
      issue: extensions as CsvValidationIssue,
      reason: error?.message || SUPPORT_ERROR_MESSAGE,
    };

    dispatch({ type: PoiBulkUploadActionTypes.OPEN_ERROR_MODAL, payload: csvError });
  };

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const base64Data = await fileToBase64(file);

    const input: BulkUploadPoisInput = {
      csv: base64Data,
      poiType: selectedTemplate.name,
      structureId,
      assemblyTemplateId: selectedAssemblyTemplateId,
      unitSystem: unitSystem,
    };

    dispatch({ type: PoiBulkUploadActionTypes.POPULATE_UPLOAD_INPUT, payload: input });
    try {
      const response = await validate({
        variables: {
          input,
        },
      });

      if (response?.error) {
        handleResponseErrors(response);
        return;
      }

      dispatch({
        type: PoiBulkUploadActionTypes.PROCEED_TO_UPLOAD_CONFIRMATION,
        payload: input,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      showSnackBar(errorMessage, 'error');
    }
  };

  const generateTemplateXlsx = () => {
    const template = filteredTemplates.find((tmp) => tmp?.id === selectedTemplate.id);
    if (!template) {
      showSnackBar('Error generating template. Please contact support', 'error');
      return;
    }

    // Use XlsxTemplateProcessor instead of CsvTemplateProcessor
    const xlsx = new XlsxTemplateProcessor(selectedTemplate as AllPointOfInterestTemplate[0]);

    // Download the XLSX file
    xlsx.download(`${selectedTemplate.name}.xlsx`);
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="poi-template-selector-modal-title"
      aria-describedby="poi-template-selector-modal-description"
    >
      <Paper sx={style}>
        {filteredTemplates?.length < 1 ? (
          <Typography variant="h5">
            No templates are currently available for this structure
          </Typography>
        ) : (
          <>
            <Typography id="poi-template-selector-modal-title" variant="h6" component="h2">
              Select POI type and upload the Excel file in {enumToString(unitSystem)}
            </Typography>
            <PoiTemplateViewer
              allPointOfInterestTemplates={filteredTemplates}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: '10%' }}>
              <ArticleIcon
                sx={{
                  fontSize: 64,
                }}
              />

              <Typography variant="body1" component="p" my={2}>
                Upload the file here, or download{' '}
                <LinkTypography onClick={generateTemplateXlsx}>template.xlsx</LinkTypography>
              </Typography>

              <Button
                variant="contained"
                sx={{ textTransform: 'none' }}
                startIcon={<FileUploadIcon />}
                onClick={onClick}
                disabled={loading}
              >
                Upload File
              </Button>
              <input
                type="file"
                ref={fileRef}
                style={{ display: 'none' }}
                onChange={onChange}
                accept=".xlsx"
              />
            </Box>
          </>
        )}
      </Paper>
    </Modal>
  );
};
